import React from "react";
import { Plus, Search, X } from "lucide-react";

import { searchSingleAssetComparisons } from "../../api/client";
import {
  filterManualComparisonCandidates,
  manualComparisonColor,
} from "../../utils/manualComparison";
import styles from "./singleAssetComparisonPicker.module.css";

export function SingleAssetComparisonPicker({
  currentInstrumentId,
  currentCode,
  excludedInstrumentIds = [],
  excludedCodes = [],
  selected,
  onChange,
  onError,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const rootRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const selectedKey = selected.map((item) => item.instrument_id).join("|");
  const excludedKey = excludedInstrumentIds.join("|");
  const excludedCodeKey = excludedCodes.join("|");

  React.useEffect(() => {
    function closeOnOutsidePointer(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, []);

  React.useEffect(() => {
    const normalized = query.trim();
    if (!open || !normalized) {
      setResults([]);
      setLoading(false);
      setActiveIndex(0);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      onError("");
      try {
        const payload = await searchSingleAssetComparisons({
          query: normalized,
          types: ["domestic_index", "global_index", "etf", "stock", "open_fund"],
          limit: 10,
        });
        if (!active) return;
        setResults(filterManualComparisonCandidates(payload?.data?.items, {
          currentInstrumentId,
          currentCode,
          selected,
          excludedInstrumentIds,
          excludedCodes,
        }));
        setActiveIndex(0);
      } catch {
        if (active) {
          setResults([]);
          onError("对比标的搜索暂时不可用。");
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [currentCode, currentInstrumentId, excludedCodeKey, excludedKey, onError, open, query, selectedKey]);

  function add(item) {
    onChange([...selected, item]);
    setQuery("");
    setResults([]);
    setActiveIndex(0);
    onError("");
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }

  function openPicker() {
    setOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleKeyDown(event) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!results.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + direction + results.length) % results.length);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      add(results[activeIndex]);
    }
  }

  return (
    <div className={styles.manualCompare} ref={rootRef}>
      {selected.map((item) => (
        <span className={styles.compareChip} key={item.instrument_id}>
          <i style={{ backgroundColor: manualComparisonColor(item.instrument_id) }} />
          <span>{item.name}</span>
          <button
            type="button"
            aria-label={`移除对比标的 ${item.name}`}
            onClick={() => onChange(selected.filter(
              (candidate) => candidate.instrument_id !== item.instrument_id,
            ))}
          >
            <X size={13} aria-hidden="true" />
          </button>
        </span>
      ))}
      <button
        className={styles.addCompareButton}
        type="button"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openPicker())}
      >
        <Plus size={14} aria-hidden="true" />添加对比
      </button>
      {open && (
        <div className={styles.comparePopover}>
          <label className={styles.compareSearch}>
            <Search size={15} aria-hidden="true" />
            <input
              ref={inputRef}
              aria-label="搜索对比标的"
              aria-controls="manual-comparison-results"
              aria-activedescendant={results[activeIndex]?.instrument_id}
              value={query}
              placeholder="搜索名称或代码"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
            />
            {loading && <span role="status">搜索中</span>}
          </label>
          {!!query.trim() && !loading && (
            <div id="manual-comparison-results" className={styles.compareResults} role="listbox">
              {!results.length && <div className={styles.compareEmpty}>没有匹配结果</div>}
              {results.map((item, index) => (
                <button
                  id={item.instrument_id}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  key={item.instrument_id}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => add(item)}
                >
                  <i style={{ backgroundColor: manualComparisonColor(item.instrument_id) }} />
                  <span><strong>{item.name}</strong><small>{item.code}</small></span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
